import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useObservableState } from 'observable-hooks';

import Maintenance from '../../../interfaces/maintenance';

import OptionModal from '../../../components/modals/option-modal';
import Readonly from '../../../components/form/elements/readonly';
import Group from '../../../components/form/elements/group';
import Link from '../../../components/form/elements/link';
import Button from '../../../components/form/elements/button';
import Form from '../../../components/form';
import Date from '../../../components/form/elements/date';
import Text from '../../../components/form/elements/text';
import Decimal from '../../../components/form/elements/decimal';
import Select from '../../../components/form/elements/select';

import { can, route } from '../../../services/utils';
import { useService } from '../../../services/services';
import {
  EQUIPMENT_MAINTENANCE_EDIT,
  EQUIPMENT_MAINTENANCE_INDEX,
} from '../../../router/routes';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';

export default function ViewMaintenance() {
  const [modal, setModal] = useState(null);
  const { t } = useTranslation();
  const history = useHistory();
  const { maintenanceService } = useService();

  const transKeyPrefix = 'pages.equipment.maintenance.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const { farmId, id } = useParams<{ farmId: string; id: string }>();
  const [maintenance] = useObservableState<Maintenance>(
    () => maintenanceService.show(farmId, id).pipe(),
    null
  );

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const formTitle = maintenance?.equipment_label ?? tp('view');

  const [bodyElements, setBodyElements] = useState([]);
  const [footerElements, setFooterElements] = useState([]);

  const deleteMaintenanceModal = useCallback(() => {
    return (
      <OptionModal
        title={tp('delete')}
        content={tp('confirm_delete')}
        buttons={[
          { title: t('yes'), name: 'yes', value: true },
          { title: t('no'), name: 'no', value: false },
        ]}
        onChoice={(choice) => {
          if (choice) {
            maintenanceService.destroy(farmId, id).subscribe(() => {
              history.push(route(EQUIPMENT_MAINTENANCE_INDEX, { farmId }));
            });
          }

          setModal(null);
        }}
        onClose={() => setModal(null)}
      />
    );
  }, [t, tp, maintenanceService, farmId, id, history]);

  const destroy = useCallback(() => {
    setModal(deleteMaintenanceModal);
  }, [deleteMaintenanceModal]);

  useEffect(() => {
    if (maintenance !== null) {
      setBodyElements([
        Readonly([
          Group([
            Date('date', transKeyPrefix + 'date'),
            Select('equipment_label', {
              label: transKeyPrefix + 'equipment',
            }),
            Text('name', transKeyPrefix + 'name'),
            Select('employee_full_name', {
              label: transKeyPrefix + 'employee',
            }),
            Decimal('spent_time', transKeyPrefix + 'spent_time'),
            Text('used_materials', transKeyPrefix + 'used_materials'),
            Decimal('material_cost', transKeyPrefix + 'material_cost'),
          ]),
        ]),
      ]);

      setFooterElements(() => {
        let elements = [];

        if (can(farmUser, { title: 'update maintenance' })) {
          elements.push(
            Link(
              route(EQUIPMENT_MAINTENANCE_EDIT, { farmId, id }),
              'edit',
              true
            )
          );
        }

        if (can(farmUser, { title: 'delete maintenance' })) {
          elements.push(Button('delete', destroy));
        }

        return elements;
      });
    }
  }, [
    farmUser,
    maintenance,
    farmId,
    id,
    transKeyPrefix,
    setBodyElements,
    destroy,
  ]);

  return (
    <>
      <Form
        mode={'view'}
        title={formTitle}
        backTo={route(EQUIPMENT_MAINTENANCE_INDEX, { farmId })}
        body={bodyElements}
        footer={footerElements}
        values={maintenance}
        submit={null}
        schema={null}
        noticeAccessor="ViewForm"
      />

      {modal}
    </>
  );
}
